import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { 
  getCategoryStats, 
  getMessagesByCategory, 
  getArchivedMemories,
  restoreArchivedMessage 
} from '../services/memoryCategoriesService';
import CategoryBadge from './memory/CategoryBadge';
import CategoryFilter from './memory/CategoryFilter';
import MilestoneMatchCard from './memory/MilestoneMatchCard';
import ReclassifyMessage from './admin/ReclassifyMessage';
import { 
  Database, 
  Archive, 
  RefreshCw, 
  RotateCcw,
  Calendar,
  TrendingUp,
  AlertCircle,
  Edit
} from 'lucide-react';
import { format } from 'date-fns';

const Memories = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [filters, setFilters] = useState({
    domain: null,
    severity: null,
    min_importance: 0
  });
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0
  });
  const [archivedPagination, setArchivedPagination] = useState({
    limit: 20,
    offset: 0
  });
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const isAdmin = user?.is_admin || user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    console.log('useEffect triggered:', { activeTab, filters, pagination });
    console.log('  - Domain filter:', filters.domain);
    console.log('  - Severity filter:', filters.severity);
    console.log('  - Min importance filter:', filters.min_importance);
    if (activeTab === 'archived') {
      fetchArchived();
    } else {
      fetchMessages();
    }
  }, [activeTab, filters, pagination]);

  const fetchStats = async () => {
    try {
      console.log('Fetching category stats from API...');
      const statsData = await getCategoryStats();
      console.log('Category stats received from API:', statsData);
      console.log('  - total_messages_classified:', statsData.total_messages_classified);
      console.log('  - total_milestone_matches:', statsData.total_milestone_matches);
      console.log('  - total_high_severity_concerns:', statsData.total_high_severity_concerns);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load category statistics');
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      let result;
      if (activeTab === 'all') {
        // If domain filter is set, use that domain instead of 'all'
        if (filters.domain) {
          result = await getMessagesByCategory(filters.domain, { ...filters, ...pagination });
        } else {
          // Fetch from all domains - we'll need to aggregate
          const domains = ['communications', 'gross_motor', 'fine_motor', 'cognitive', 'personal_social', 'general'];
          const promises = domains.map(domain => 
            getMessagesByCategory(domain, { 
              ...filters, 
              domain: null, // Don't filter by domain when fetching individual domains
              ...pagination 
            })
          );
          const results = await Promise.all(promises);
          // Combine results
          const allMessages = results.flatMap(r => r.messages || []);
          const totalCount = results.reduce((sum, r) => sum + (r.total || 0), 0);
          
          // Apply client-side filters (severity, min_importance) if needed
          let filteredMessages = allMessages;
          if (filters.severity) {
            filteredMessages = filteredMessages.filter(msg => 
              msg.categories?.severity === filters.severity
            );
          }
          if (filters.min_importance > 0) {
            filteredMessages = filteredMessages.filter(msg => 
              (msg.importance_score || 0) >= filters.min_importance
            );
          }
          
          // Remove duplicates by message ID
          const uniqueMessages = Array.from(
            new Map(filteredMessages.map(msg => [msg.id, msg])).values()
          );
          
          // Sort by created_at descending
          uniqueMessages.sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          
          result = {
            messages: uniqueMessages,
            total: uniqueMessages.length
          };
        }
      } else {
        result = await getMessagesByCategory(activeTab, { ...filters, ...pagination });
      }
      console.log('Fetched messages result:', result);
      console.log('Messages array:', result.messages);
      console.log('Messages count:', result.messages?.length || 0);
      setMessages(result.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchived = async () => {
    setLoadingArchived(true);
    try {
      const result = await getArchivedMemories(archivedPagination);
      setArchivedMessages(result.messages || []);
    } catch (error) {
      console.error('Failed to fetch archived:', error);
      toast.error('Failed to load archived messages');
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleRestore = async (messageId) => {
    try {
      await restoreArchivedMessage(messageId);
      toast.success('Message restored successfully');
      fetchArchived();
      fetchStats();
    } catch (error) {
      toast.error('Failed to restore message');
      console.error(error);
    }
  };

  const handleClearFilters = () => {
    console.log('Clearing filters');
    setFilters({
      domain: null,
      severity: null,
      min_importance: 0
    });
  };

  const handleReclassify = (message) => {
    setSelectedMessage(message);
    setReclassifyOpen(true);
  };

  const handleReclassifySuccess = () => {
    fetchMessages();
    fetchStats();
  };

  const domains = [
    { value: 'all', label: 'All Memories' },
    { value: 'communications', label: 'Communications' },
    { value: 'gross_motor', label: 'Gross Motor' },
    { value: 'fine_motor', label: 'Fine Motor' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'personal_social', label: 'Personal/Social' },
    { value: 'general', label: 'General' }
  ];

  const renderMessage = (message) => {
    if (!message || !message.id) {
      console.warn('Invalid message in renderMessage:', message);
      return null;
    }
    const categories = message.categories || {};
    const matchedMilestones = categories.matched_milestones || [];
    const importanceScore = message.importance_score || 0;

    return (
      <Card key={message.id} className="mb-4">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CategoryBadge 
                    domain={categories.primary} 
                    severity={categories.severity}
                  />
                  {importanceScore > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <TrendingUp className="h-3 w-3" />
                      {(importanceScore * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReclassify(message)}
                    className="h-7 px-2"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <CardDescription className="text-xs">
                {format(new Date(message.created_at), 'PPp')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-3">{message.content}</p>
          
          {matchedMilestones.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-600 mb-2">
                Matched Milestones ({matchedMilestones.length}):
              </p>
              {matchedMilestones.map((milestone, idx) => (
                <MilestoneMatchCard key={idx} milestone={milestone} />
              ))}
            </div>
          )}

          {categories.secondary_tags && categories.secondary_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {categories.secondary_tags.map((tag, idx) => (
                <span 
                  key={idx}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderArchivedMessage = (message) => {
    const categories = message.categories || {};
    
    return (
      <Card key={message.id} className="mb-4">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CategoryBadge 
                  domain={categories.primary} 
                  severity={categories.severity}
                />
                <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                  Archived: {message.archive_reason}
                </span>
              </div>
              <CardDescription className="text-xs">
                {format(new Date(message.archived_at), 'PPp')}
              </CardDescription>
            </div>
            {message.can_restore && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRestore(message.id)}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Restore
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{message.content}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Memories</h1>
        <p className="text-gray-600">
          View and manage your categorized conversation memories
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_messages_classified || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Milestone Matches</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_milestone_matches || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">High Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.total_high_severity_concerns || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Archived</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {archivedMessages.length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <CategoryFilter 
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={handleClearFilters}
          />
        </div>

        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-8">
              {domains.map((domain) => (
                <TabsTrigger key={domain.value} value={domain.value} className="text-xs">
                  {domain.label}
                </TabsTrigger>
              ))}
              <TabsTrigger value="archived" className="text-xs">
                <Archive className="h-3 w-3 mr-1" />
                Archived
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i}>
                      <CardHeader>
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24 mt-2" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-20 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : activeTab === 'archived' ? (
                <div>
                  {loadingArchived ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32 w-full" />
                      ))}
                    </div>
                  ) : archivedMessages.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center text-gray-500">
                        <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No archived messages</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div>
                      {archivedMessages.map(renderArchivedMessage)}
                      <div className="flex justify-center mt-4">
                        <Button
                          variant="outline"
                          onClick={() => setArchivedPagination({
                            ...archivedPagination,
                            offset: archivedPagination.offset + archivedPagination.limit
                          })}
                        >
                          Load More
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : messages.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No messages found</p>
                    <p className="text-xs mt-1">Try adjusting your filters</p>
                    <p className="text-xs mt-1 text-gray-400">Debug: activeTab={activeTab}, messages.length={messages.length}</p>
                  </CardContent>
                </Card>
              ) : (
                <div>
                  {messages
                    .filter(msg => msg && msg.id)
                    .map(renderMessage)
                    .filter(Boolean)}
                  <div className="flex justify-center mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setPagination({
                        ...pagination,
                        offset: pagination.offset + pagination.limit
                      })}
                    >
                      Load More
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ReclassifyMessage
        message={selectedMessage}
        open={reclassifyOpen}
        onOpenChange={setReclassifyOpen}
        onSuccess={handleReclassifySuccess}
      />
    </div>
  );
};

export default Memories;

